import { Suspense, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ShieldExclamationIcon } from '@heroicons/react/24/outline';

import { useStateContext } from '../../contexts/ContextProvider';
import { formatDayMonthInLocale } from '../../helpers/FormatDate';
import lazyWithRetry from '../../helpers/lazyWithRetry';

// Chargée à l'ouverture seulement (qrcode.react), comme depuis le menu utilisateur.
const AccountSecurityModal = lazyWithRetry(() => import('../../pages/auth/twoFactor/AccountSecurityModal'));

/**
 * Bandeau de grâce : la 2FA est obligatoire pour ce compte et non conforme, soit
 * absente, soit activée par e-mail alors que le rôle exige une application
 * (`compliant`).
 *
 * Non fermable (ADR 0005) : il disparaît quand le compte devient conforme. Masqué
 * pour une session ouverte par le SSO, dont la MFA relève du fournisseur d'identité.
 */
export default function TwoFactorGraceBanner() {
    const { t, i18n } = useTranslation('auth');
    const { user } = useStateContext();
    const [isSecurityOpen, setIsSecurityOpen] = useState(false);

    const isCompliant = user?.two_factor_compliant ?? user?.two_factor_enabled;
    // Ne PAS `return null` avant la modale : sinon dès que le compte devient conforme
    // (ex : activation en cours via cette même modale), le bandeau démonte la modale
    // avec lui et l'utilisateur ne voit jamais ses codes de secours (bug prod
    // 2026-09-28). La modale reste montée tant que `isSecurityOpen` est vrai, même
    // quand le bandeau ne doit plus s'afficher.
    const showBanner = Boolean(user?.two_factor_required) && !isCompliant && !user?.session_via_sso;
    const isUpgrade = Boolean(user?.two_factor_enabled);

    const deadline = user?.two_factor_deadline_at ? formatDayMonthInLocale(user.two_factor_deadline_at, i18n.language) : null;

    return (
        <>
            {showBanner && (
                <div role="alert" className="border-b border-amber-200 bg-amber-50 px-4 py-2.5">
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-amber-900">
                        <ShieldExclamationIcon className="h-5 w-5 flex-shrink-0 text-amber-600" aria-hidden="true" />
                        <span className="flex-1">
                            {deadline
                                ? t(isUpgrade ? 'two_factor_grace.upgrade_message' : 'two_factor_grace.message', { date: deadline })
                                : t(isUpgrade ? 'two_factor_grace.upgrade_message_no_date' : 'two_factor_grace.message_no_date')}
                        </span>
                        <button
                            type="button"
                            onClick={() => setIsSecurityOpen(true)}
                            className="rounded-lg bg-primary-color px-3 py-1.5 text-sm font-semibold text-white hover:bg-primary-color-light"
                        >
                            {t(isUpgrade ? 'two_factor_grace.cta_upgrade' : 'two_factor_grace.cta')}
                        </button>
                    </div>
                </div>
            )}

            {isSecurityOpen && (
                <Suspense fallback={null}>
                    <AccountSecurityModal open={isSecurityOpen} setOpen={setIsSecurityOpen} />
                </Suspense>
            )}
        </>
    );
}
